import moment from "moment-timezone";

/**
 * Parse date with multiple format support (Firestore timestamps, etc.)
 */
export const parseDate = (dateInput, timezone = null) => {
  if (!dateInput) return null;

  if (dateInput._seconds) {
    return timezone
      ? moment.unix(dateInput._seconds).tz(timezone)
      : moment.unix(dateInput._seconds);
  }

  if (dateInput.toDate) {
    return timezone
      ? moment(dateInput.toDate()).tz(timezone)
      : moment(dateInput.toDate());
  }

  return timezone ? moment(dateInput).tz(timezone) : moment(dateInput);
};

/**
 * Check if a time slot is blocked by any blocked periods
 */
export const isSlotBlocked = (
  slotStart,
  slotEnd,
  blockedPeriods,
  selectedDate,
  timezone,
) => {
  if (!blockedPeriods || blockedPeriods.length === 0) return false;

  return blockedPeriods.some((period) => {
    if (period.type === "time_specific" || period.type === "meeting_booked") {
      // All-day events: use the raw YYYY-MM-DD strings from Google for a
      // timezone-safe comparison.  Timestamp-based checks shift by UTC offset
      // and break for users outside UTC.
      if (period.isAllDay && period.allDayStart) {
        const selectedStr = selectedDate.format("YYYY-MM-DD");
        const endStr = period.allDayEnd || period.allDayStart;
        return selectedStr >= period.allDayStart && selectedStr <= endStr;
      }

      const blockStart = parseDate(period.start_time, timezone);
      const blockEnd = parseDate(period.end_time, timezone);

      if (!blockStart || !blockEnd) return false;

      // Legacy calendar_auto blocks (synced before the isAllDay metadata was
      // added) don't have allDayStart/allDayEnd.  The old day pre-filter used
      // period.date which is a UTC midnight timestamp — it shifts by the UTC
      // offset for non-UTC users, causing all-day events to be silently skipped.
      // Fix: for calendar_auto, check whether selectedDate falls within the
      // timezone-adjusted day span of the block, then do the overlap check.
      if (period.source === "calendar_auto") {
        if (
          !selectedDate.isBetween(
            blockStart.clone().startOf("day"),
            blockEnd.clone().endOf("day"),
            null,
            "[]",
          )
        ) {
          return false;
        }
        return slotStart.isBefore(blockEnd) && slotEnd.isAfter(blockStart);
      }

      // Manual / meeting blocks: standard day pre-filter is safe because
      // those timestamps are stored in the user's own timezone context.
      const blockDate = parseDate(period.date, timezone);
      if (!blockDate || !blockDate.isSame(selectedDate, "day")) return false;

      return slotStart.isBefore(blockEnd) && slotEnd.isAfter(blockStart);
    }
    return false;
  });
};

/**
 * Check if a day is blocked by date-range blocks
 */
export const isDayBlockedByDateRange = (day, blockedPeriods) => {
  if (!blockedPeriods || blockedPeriods.length === 0) return false;

  return blockedPeriods.some((period) => {
    if (period.start_date && period.end_date) {
      const periodStart = parseDate(period.start_date);
      const periodEnd = parseDate(period.end_date);

      if (!periodStart || !periodEnd) return false;

      return day.isBetween(periodStart, periodEnd, "day", "[]");
    }
    return false;
  });
};
